import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  query,
  where,
  updateDoc,
} from "firebase/firestore";
import { firestore } from "../firebase";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";

// --- NEW MUI IMPORTS ---
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AssignmentInd as AssignmentIndIcon,
} from "@mui/icons-material";
// --- END NEW MUI IMPORTS ---

const AMCScheduleList = () => {
  const [schedules, setSchedules] = useState([]);
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [filterEmail, setFilterEmail] = useState("");
  const [facilityAdmins, setFacilityAdmins] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedScheduleForAssign, setSelectedScheduleForAssign] = useState(null);
  const [selectedFacilityAdmin, setSelectedFacilityAdmin] = useState("");
  const [assigningLoading, setAssigningLoading] = useState(false);
  const [assignTechnicianDialogOpen, setAssignTechnicianDialogOpen] = useState(false);
  const [selectedScheduleForTechAssign, setSelectedScheduleForTechAssign] = useState(null);
  const [selectedTechnician, setSelectedTechnician] = useState("");
  const [availableChecklists, setAvailableChecklists] = useState([]);
  const [selectedChecklist, setSelectedChecklist] = useState("");
  const [assigningTechnicianLoading, setAssigningTechnicianLoading] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [currentUserDocId, setCurrentUserDocId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const assignedOnlyView =
    new URLSearchParams(location.search).get("assignedOnly") === "true";
  const normalizeRoleKey = (role) =>
    String(role || "").trim().toLowerCase().replace(/\s+/g, "_");
  const isTechnicianRole = (role) =>
    normalizeRoleKey(role) === "technician";
  const normalizeValue = (value) => String(value || "").trim().toLowerCase();

  const getStoredUserProfile = () => {
    try {
      return JSON.parse(sessionStorage.getItem("userProfile") || "{}");
    } catch {
      return {};
    }
  };

  const isScheduleAssignedToTechnician = (schedule, technician) => {
    const technicianValues = new Set(
      [
        technician.email,
        technician.docId,
        technician.displayName,
        technician.adminName,
        technician.name,
      ]
        .map(normalizeValue)
        .filter(Boolean)
    );

    const topLevelValues = [
      schedule.assignedTechnicianEmail,
      schedule.assignedTechnicianId,
      schedule.assignedTechnicianName,
    ].map(normalizeValue);

    if (topLevelValues.some((value) => technicianValues.has(value))) {
      return true;
    }

    const dueRows = Array.isArray(schedule.DueDatesTable)
      ? schedule.DueDatesTable
      : [];

    return dueRows.some((row) =>
      [row.AssignedTo, row.AssignedToName, row.assignedTo, row.assignedToName]
        .map(normalizeValue)
        .some((value) => technicianValues.has(value))
    );
  };

  const hasFacilityAssignColumn = userRole === "admin" || userRole === "superadmin";
  const hasTechnicianAssignColumn = userRole === "facility_admin";
  const isCustomerView =
    !["admin", "superadmin", "facility_admin"].includes(userRole) &&
    !isTechnicianRole(userRole);
  const hasCustomerTechnicianColumn = isCustomerView;
  const visibleColumnsCount =
    7 +
    (hasFacilityAssignColumn ? 1 : 0) +
    (hasTechnicianAssignColumn ? 1 : 0) +
    (hasCustomerTechnicianColumn ? 1 : 0);

  // Helper function to format date as dd/mon/yyyy
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const fetchFacilityAdmins = async () => {
    try {
      const q = query(
        collection(firestore, "UserProfileMaster"),
        where("role", "==", "facility_admin")
      );
      const snapshot = await getDocs(q);
      const admins = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setFacilityAdmins(admins);
    } catch (error) {
      console.error("Error fetching facility admins:", error);
    }
  };

  const fetchTechnicians = async (facilityAdminId = null) => {
    try {
      const constraints = [
        where("role", "in", ["technician", "Technician"]),
      ];
      if (facilityAdminId) {
        constraints.push(where("facilityId", "==", facilityAdminId));
      }
      const q = query(collection(firestore, "UserProfileMaster"), ...constraints);
      const snapshot = await getDocs(q);
      const techs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTechnicians(techs);
    } catch (error) {
      console.error("Error fetching technicians:", error);
    }
  };

  const fetchAvailableChecklists = async (email, role) => {
    try {
      const tenantId = sessionStorage.getItem("tenantId");
      const checklistDocsById = new Map();
      const addChecklistDocs = (snapshot) => {
        snapshot.docs.forEach((entry) => {
          checklistDocsById.set(entry.id, {
            id: entry.id,
            ...entry.data(),
          });
        });
      };

      if (role === "superadmin") {
        addChecklistDocs(await getDocs(collection(firestore, "checklists")));
      } else {
        if (tenantId) {
          addChecklistDocs(
            await getDocs(
              query(
                collection(firestore, "checklists"),
                where("tenantId", "==", tenantId)
              )
            )
          );
        }

        if (email) {
          addChecklistDocs(
            await getDocs(
              query(
                collection(firestore, "checklists"),
                where("createdBy", "==", email)
              )
            )
          );
          addChecklistDocs(
            await getDocs(
              query(
                collection(firestore, "checklists"),
                where("assignedTo", "==", email)
              )
            )
          );
        }
      }

      const checklists = Array.from(checklistDocsById.values())
        .filter((checklist) => checklist.IsActive !== false)
        .map((checklist) => ({
          id: checklist.id,
          name: checklist.name || "Unnamed Checklist",
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setAvailableChecklists(checklists);
    } catch (error) {
      console.error("Error fetching checklists:", error);
    }
  };

  const fetchSchedules = async (userId, userRole, facilityAdminDocId, userProfile = {}) => {
    try {
      setLoading(true);
      let snapshots = [];

      if (userRole === "admin" || userRole === "superadmin") {
        snapshots = [await getDocs(query(collection(firestore, "AMCScheduleMaster")))];
      } else if (userRole === "facility_admin") {
        // Facility admin should see schedules assigned to them
        snapshots = [
          await getDocs(
            query(
              collection(firestore, "AMCScheduleMaster"),
              where("assignedFacilityAdminId", "==", facilityAdminDocId)
            )
          ),
        ];
      } else if (isTechnicianRole(userRole)) {
        snapshots = [await getDocs(query(collection(firestore, "AMCScheduleMaster")))];
      } else {
        snapshots = [
          await getDocs(
            query(
              collection(firestore, "AMCScheduleMaster"),
              where("createdBy", "==", userId)
            )
          ),
        ];
      }

      const scheduleMap = new Map();
      snapshots.forEach((snapshot) => {
        snapshot.docs.forEach((entry) => {
          scheduleMap.set(entry.id, { id: entry.id, ...entry.data() });
        });
      });
      const data = isTechnicianRole(userRole)
        ? Array.from(scheduleMap.values()).filter((schedule) =>
            isScheduleAssignedToTechnician(schedule, {
              ...userProfile,
              email: userId,
              docId: facilityAdminDocId,
            })
          )
        : Array.from(scheduleMap.values());

      const sortedData = data.sort((a, b) => {
        const dateA = a.createdAt || a.AMCScheduleStartDate || new Date(0);
        const dateB = b.createdAt || b.AMCScheduleStartDate || new Date(0);
        return new Date(dateB) - new Date(dateA);
      });

      setSchedules(sortedData);
      setFilteredSchedules(
        applyFilter(sortedData, filterEmail, showActiveOnly, assignedOnlyView)
      );
    } catch (error) {
      console.error("Error fetching schedules:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const auth = getAuth();
    const getPreferredProfile = async (email) => {
      const storedProfile = getStoredUserProfile();
      const normalizedEmail = normalizeValue(email);
      const profileById = new Map();
      const emailValues = Array.from(
        new Set(
          [
            email,
            normalizedEmail,
            storedProfile.email,
          ].filter(Boolean)
        )
      );

      emailValues.forEach((emailValue) => {
        if (normalizeValue(emailValue) === normalizeValue(storedProfile.email)) {
          profileById.set(storedProfile.id || "stored", storedProfile);
        }
      });

      await Promise.all(
        emailValues.map(async (emailValue) => {
          const q = query(
            collection(firestore, "UserProfileMaster"),
            where("email", "==", emailValue)
          );
          const snapshot = await getDocs(q);
          snapshot.docs.forEach((profileDoc) => {
            profileById.set(profileDoc.id, { id: profileDoc.id, ...profileDoc.data() });
          });
        })
      );

      const profiles = Array.from(profileById.values()).filter(
        (profile) => profile && Object.keys(profile).length
      );

      return (
        profiles.find((profile) => isTechnicianRole(profile.role)) ||
        profiles[0] ||
        {}
      );
    };

    const loadSchedulesForSession = async (email, role) => {
      const normalizedRole = normalizeRoleKey(role);

      if (!email || !normalizedRole) {
        setLoading(false);
        setSchedules([]);
        setFilteredSchedules([]);
        setUserRole(null);
        return;
      }

      setUserRole(normalizedRole);

      if (normalizedRole === "facility_admin" || isTechnicianRole(normalizedRole)) {
        try {
          const profile = await getPreferredProfile(email);
          const docId = profile.id || null;
          setCurrentUserDocId(docId);
          await fetchSchedules(email, normalizedRole, docId, profile);
          if (normalizedRole === "facility_admin") {
            fetchTechnicians(docId);
            fetchAvailableChecklists(email, normalizedRole);
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
          setLoading(false);
        }
      } else {
        fetchSchedules(email, normalizedRole, null);
        fetchAvailableChecklists(email, normalizedRole);
      }

      fetchFacilityAdmins();
    };

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const role = sessionStorage.getItem("userRole");
      const sessionEmail = sessionStorage.getItem("userEmail");

      if (user?.email) {
        loadSchedulesForSession(user.email, role);
      } else if (sessionEmail && role) {
        loadSchedulesForSession(sessionEmail, role);
      } else {
        setLoading(false);
        setSchedules([]);
        setFilteredSchedules([]);
        setUserRole(null);
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this schedule?")) {
      try {
        await deleteDoc(doc(firestore, "AMCScheduleMaster", id));
        const auth = getAuth();
        const user = auth.currentUser;
        if (user) {
          await fetchSchedules(user.email, userRole, currentUserDocId);
        }
      } catch (error) {
        console.error("Error deleting schedule:", error);
      }
    }
  };

  const handleOpenAssignDialog = (schedule) => {
    // Only admins can assign
    if (userRole !== "admin" && userRole !== "superadmin") {
      return;
    }
    setSelectedScheduleForAssign(schedule);
    setSelectedFacilityAdmin(schedule.assignedFacilityAdminId || "");
    setAssignDialogOpen(true);
  };

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setSelectedScheduleForAssign(null);
    setSelectedFacilityAdmin("");
  };

  const handleOpenAssignTechnicianDialog = (schedule) => {
    if (userRole !== "facility_admin") {
      return;
    }
    setSelectedScheduleForTechAssign(schedule);
    setSelectedTechnician(schedule.assignedTechnicianId || "");
    setSelectedChecklist(
      schedule.assignedChecklistId ||
        schedule.DueDatesTable?.find((row) => row.AssignedChecklist)
          ?.AssignedChecklist ||
        ""
    );
    setAssignTechnicianDialogOpen(true);
  };

  const handleCloseAssignTechnicianDialog = () => {
    setAssignTechnicianDialogOpen(false);
    setSelectedScheduleForTechAssign(null);
    setSelectedTechnician("");
    setSelectedChecklist("");
  };

  const handleAssignFacilityAdmin = async () => {
    // Only admins can assign
    if (userRole !== "admin" && userRole !== "superadmin") {
      alert("You do not have permission to assign AMC schedules.");
      return;
    }

    if (!selectedScheduleForAssign || !selectedFacilityAdmin) {
      alert("Please select a facility admin");
      return;
    }

    setAssigningLoading(true);
    try {
      const selectedAdmin = facilityAdmins.find(
        (admin) => admin.id === selectedFacilityAdmin
      );
      
      await updateDoc(
        doc(firestore, "AMCScheduleMaster", selectedScheduleForAssign.id),
        {
          assignedFacilityAdminId: selectedFacilityAdmin,
          assignedFacilityAdminName: selectedAdmin?.adminName || selectedAdmin?.displayName || "",
          assignedFacilityAdminEmail: selectedAdmin?.email || "",
          assignedAt: new Date(),
        }
      );

      // Refetch schedules to show updated data
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        await fetchSchedules(user.email, userRole, currentUserDocId);
      }

      handleCloseAssignDialog();
      alert("AMC Schedule assigned successfully!");
    } catch (error) {
      console.error("Error assigning facility admin:", error);
      alert("Error assigning facility admin. Please try again.");
    } finally {
      setAssigningLoading(false);
    }
  };

  const handleAssignTechnician = async () => {
    if (!["facility_admin", "admin", "superadmin"].includes(userRole)) {
      alert("You do not have permission to assign a technician.");
      return;
    }

    if (!selectedScheduleForTechAssign || (!selectedTechnician && !selectedChecklist)) {
      alert("Please select a technician or checklist");
      return;
    }

    setAssigningTechnicianLoading(true);
    try {
      const selectedTech = technicians.find(
        (tech) => tech.id === selectedTechnician
      );
      const selectedChecklistDetails = availableChecklists.find(
        (checklist) => checklist.id === selectedChecklist
      );

      const updatedDueDatesTable = selectedScheduleForTechAssign.DueDatesTable
        ? selectedScheduleForTechAssign.DueDatesTable.map((row) => ({
            ...row,
            AssignedTo: selectedTech
              ? selectedTech.email || selectedTech.id || ""
              : row.AssignedTo || "",
            AssignedToName:
              selectedTech
                ? selectedTech.displayName || selectedTech.name || selectedTech.email || ""
                : row.AssignedToName || "",
            AssignedChecklist: selectedChecklist || row.AssignedChecklist || "",
            AssignedChecklistName:
              selectedChecklistDetails?.name || row.AssignedChecklistName || "",
          }))
        : selectedScheduleForTechAssign.DueDatesTable;

      const updatePayload = {
        DueDatesTable: updatedDueDatesTable,
      };

      if (selectedTech) {
        updatePayload.assignedTechnicianId = selectedTechnician;
        updatePayload.assignedTechnicianName =
          selectedTech.displayName || selectedTech.name || selectedTech.email || "";
        updatePayload.assignedTechnicianEmail = selectedTech.email || "";
        updatePayload.assignedTechnicianAt = new Date();
      }

      if (selectedChecklistDetails) {
        updatePayload.assignedChecklistId = selectedChecklist;
        updatePayload.assignedChecklistName = selectedChecklistDetails.name;
        updatePayload.assignedChecklistAt = new Date();
      }

      await updateDoc(
        doc(firestore, "AMCScheduleMaster", selectedScheduleForTechAssign.id),
        updatePayload
      );

      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        await fetchSchedules(user.email, userRole, currentUserDocId);
      }

      handleCloseAssignTechnicianDialog();
      alert("Assignment updated successfully!");
    } catch (error) {
      console.error("Error assigning technician:", error);
      alert("Error assigning technician. Please try again.");
    } finally {
      setAssigningTechnicianLoading(false);
    }
  };

  const handleEdit = (id) => {
    navigate(`/amc/schedule/edit/${id}`);
  };

  const handleView = (id) => {
    navigate(`/amc/schedule/view/${id}`);
  };

  const handleCreate = () => {
    navigate("/amc/schedule/new");
  };

  const handleFilterChange = (e) => {
    setFilterEmail(e.target.value.toLowerCase());
  };

  // Apply filters and update state
  const handleGoButton = () => {
    setFilteredSchedules(
      applyFilter(schedules, filterEmail, showActiveOnly, assignedOnlyView)
    );
  };

  // Clear filter text and re-apply filters
  const handleClearFilter = () => {
    setFilterEmail("");
    setFilteredSchedules(
      applyFilter(schedules, "", showActiveOnly, assignedOnlyView)
    );
  };

  // Toggle active filter and re-apply filters
  const handleActiveToggle = (e) => {
    const checked = e.target.checked;
    setShowActiveOnly(checked);
    setFilteredSchedules(
      applyFilter(schedules, filterEmail, checked, assignedOnlyView)
    );
  };

  // Pure function to apply filters
  const applyFilter = (data, emailFilter, activeOnly, assignedOnly) => {
    return data.filter((schedule) => {
      const emailOk =
        !emailFilter ||
        schedule.CustomerEmailID?.toLowerCase().includes(emailFilter);
      const activeOk = !activeOnly || schedule.IsActive === true;
      const assignedOk =
        !assignedOnly || Boolean(schedule.assignedFacilityAdminId);
      return emailOk && activeOk && assignedOk;
    });
  };

  useEffect(() => {
    setFilteredSchedules(
      applyFilter(schedules, filterEmail, showActiveOnly, assignedOnlyView)
    );
  }, [schedules, filterEmail, showActiveOnly, assignedOnlyView]);

  return (
    // Main container box with a light gray background
    <Box sx={{ flexGrow: 1, p: 3, bgcolor: "grey.100" }}>
      <Container maxWidth="xl">
        {/* Paper component provides the white card effect */}
        <Paper sx={{ p: { xs: 2, md: 3 }, boxShadow: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Maintenance Schedules
          </Typography>

          {/* Filter Bar */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap", // Allow wrapping on small screens
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                label="Filter by Customer Email"
                variant="outlined"
                size="small"
                value={filterEmail}
                onChange={handleFilterChange}
                onKeyPress={(e) => {
                  if (e.key === "Enter") handleGoButton();
                }}
              />
              <Button
                variant="contained"
                onClick={handleGoButton}
                title="Search schedules"
              >
                Go
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilter}
                title="Clear filter"
              >
                Clear
              </Button>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showActiveOnly}
                    onChange={handleActiveToggle}
                  />
                }
                label="Active only"
              />
              <Tooltip title="Create New Schedule">
                <span>
                  <IconButton
                    onClick={handleCreate}
                    aria-label="add"
                    disabled={isTechnicianRole(userRole)}
                    sx={{
                      background: "linear-gradient(to right, #6A5ACD, #FF69B4)",
                      color: "white",
                      "&:hover": {
                        background: "linear-gradient(to right, #5a4bcd, #ef59a4)",
                      },
                      "&.Mui-disabled": {
                        bgcolor: "grey.300",
                        background: "none",
                        color: "grey.500",
                      },
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {/* Table Container */}
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>AMC Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Customer Email</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Frequency</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Active</TableCell>
                  {userRole === "admin" || userRole === "superadmin" ? (
                    <TableCell sx={{ fontWeight: "bold" }}>Assign To</TableCell>
                  ) : null}
                  {hasTechnicianAssignColumn ? (
                    <TableCell sx={{ fontWeight: "bold" }}>
                      <div style={{ lineHeight: 1.1 }}>
                        Assigned
                        <br />
                        Technician
                      </div>
                    </TableCell>
                  ) : null}
                  {hasCustomerTechnicianColumn ? (
                    <TableCell sx={{ fontWeight: "bold" }}>
                      <div style={{ lineHeight: 1.1 }}>
                        Assigned
                        <br />
                        Technician
                      </div>
                    </TableCell>
                  ) : null}
                  <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnsCount} align="center" sx={{ p: 5 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredSchedules.length > 0 ? (
                  filteredSchedules.map((schedule) => (
                    <TableRow hover key={schedule.id}>
                      <TableCell>{schedule.AMCName}</TableCell>
                      <TableCell>{schedule.CustomerName}</TableCell>
                      <TableCell>{schedule.CustomerEmailID}</TableCell>
                      <TableCell>
                        {formatDate(schedule.AMCScheduleStartDate)}
                      </TableCell>
                      <TableCell>
                        {schedule.FreqOfMaintenance}{" "}
                        {schedule.UnitForDurationOfContract}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={schedule.IsActive ? "Active" : "Inactive"}
                          color={schedule.IsActive ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>
                      {userRole === "admin" || userRole === "superadmin" ? (
                        <TableCell>
                          <Chip
                            label={
                              schedule.assignedFacilityAdminName || "Not Assigned"
                            }
                            variant="outlined"
                            size="small"
                            icon={<AssignmentIndIcon />}
                            onClick={() => handleOpenAssignDialog(schedule)}
                            sx={{ cursor: "pointer" }}
                          />
                        </TableCell>
                      ) : null}
                      {hasTechnicianAssignColumn ? (
                        <TableCell>
                          {schedule.assignedTechnicianName ? (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              <Chip
                                label={
                                  schedule.assignedTechnicianName || schedule.assignedTechnicianEmail || "Assigned"
                                }
                                variant="outlined"
                                size="small"
                                icon={<AssignmentIndIcon />}
                              />
                              {schedule.assignedChecklistName ||
                              schedule.DueDatesTable?.find((row) => row.AssignedChecklistName)
                                ?.AssignedChecklistName ? (
                                <Chip
                                  label={
                                    schedule.assignedChecklistName ||
                                    schedule.DueDatesTable?.find((row) => row.AssignedChecklistName)
                                      ?.AssignedChecklistName
                                  }
                                  variant="outlined"
                                  size="small"
                                  color="secondary"
                                />
                              ) : null}
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleOpenAssignTechnicianDialog(schedule)}
                              >
                                Reassign
                              </Button>
                            </Box>
                          ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              {schedule.assignedChecklistName ||
                              schedule.DueDatesTable?.find((row) => row.AssignedChecklistName)
                                ?.AssignedChecklistName ? (
                                <Chip
                                  label={
                                    schedule.assignedChecklistName ||
                                    schedule.DueDatesTable?.find((row) => row.AssignedChecklistName)
                                      ?.AssignedChecklistName
                                  }
                                  variant="outlined"
                                  size="small"
                                  color="secondary"
                                />
                              ) : null}
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleOpenAssignTechnicianDialog(schedule)}
                              >
                                Assign
                              </Button>
                            </Box>
                          )}
                        </TableCell>
                      ) : null}
                      {hasCustomerTechnicianColumn ? (
                        <TableCell>
                          {schedule.assignedTechnicianName || schedule.assignedTechnicianEmail ? (
                            schedule.assignedTechnicianName || schedule.assignedTechnicianEmail
                          ) : (
                            <Chip label="Will be updated" size="small" />
                          )}
                        </TableCell>
                      ) : null}
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={() => handleView(schedule.id)}
                            >
                              <VisibilityIcon color="info" />
                            </IconButton>
                          </Tooltip>
                          {!isTechnicianRole(userRole) ? (
                            <>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(schedule.id)}
                                >
                                  <EditIcon color="warning" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(schedule.id)}
                                >
                                  <DeleteIcon color="error" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : null}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={visibleColumnsCount} align="center" sx={{ p: 5 }}>
                      No schedules found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>

      {/* Assign Facility Admin Dialog - Admin Only */}
      {userRole === "admin" || userRole === "superadmin" ? (
        <Dialog open={assignDialogOpen} onClose={handleCloseAssignDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Assign Maintenance Schedule to Facility Admin</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Select Facility Admin</InputLabel>
              <Select
                value={selectedFacilityAdmin}
                label="Select Facility Admin"
                onChange={(e) => setSelectedFacilityAdmin(e.target.value)}
              >
                <MenuItem value="">
                  <em>Keep Current Technician</em>
                </MenuItem>
                {facilityAdmins.map((admin) => (
                  <MenuItem key={admin.id} value={admin.id}>
                    {admin.adminName || admin.displayName} ({admin.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAssignDialog}>Cancel</Button>
            <Button
              onClick={handleAssignFacilityAdmin}
              variant="contained"
              disabled={assigningLoading}
            >
              {assigningLoading ? "Assigning..." : "Assign"}
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}

      {/* Assign Technician Dialog */}
      {hasTechnicianAssignColumn ? (
        <Dialog
          open={assignTechnicianDialogOpen}
          onClose={handleCloseAssignTechnicianDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Assign Technician</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Technician</InputLabel>
              <Select
                value={selectedTechnician}
                label="Select Technician"
                onChange={(e) => setSelectedTechnician(e.target.value)}
              >
                <MenuItem value="">
                  <em>None - Remove Assignment</em>
                </MenuItem>
                {technicians.map((tech) => (
                  <MenuItem key={tech.id} value={tech.id}>
                    {tech.displayName || tech.name || tech.email} ({tech.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
           
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAssignTechnicianDialog}>Cancel</Button>
            <Button
              onClick={handleAssignTechnician}
              variant="contained"
              disabled={assigningTechnicianLoading}
            >
              {assigningTechnicianLoading ? "Assigning..." : "Assign"}
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </Box>
  );
};

export default AMCScheduleList;
